/**
 * A file handed across a call: what one is, and the rules about one.
 *
 * Files ride the room's data channel as LiveKit byte streams, the same way the
 * chat and the whiteboard ride it. Nothing is uploaded and nothing is kept —
 * a file lives in the browsers holding the call and ends with it. What is
 * ours rather than the transport's is which files may cross, and how a name
 * that came from the other side is made safe to save under.
 */

/** The stream topic files travel on. Anything else in the room ignores it. */
export const MEETING_FILE_TOPIC = "alchely-file";

/**
 * How big a file may be.
 *
 * Enough for a scanned worksheet or a phone photo. The channel is shared with
 * the chat and the board, and the receiver holds the whole file in memory, so
 * this is a ceiling on how long a transfer can crowd the call as much as on
 * size.
 */
export const MAX_MEETING_FILE_BYTES = 10 * 1024 * 1024;

/** Longest name a received file is saved under. */
export const MAX_MEETING_FILE_NAME_LENGTH = 120;

/**
 * What may be shared: documents and pictures.
 *
 * An allowlist by extension, because the declared MIME type is whatever the
 * sending browser — or a modified client — says it is. Nothing here renders
 * as a page or runs when opened (no html, svg, scripts or executables), and
 * macro-enabled office formats are left out on purpose.
 */
export const ALLOWED_MEETING_FILE_EXTENSIONS: readonly string[] = [
    "pdf",
    "jpg",
    "jpeg",
    "png",
    "webp",
    "gif",
    "heic",
    "txt",
    "rtf",
    "doc",
    "docx",
    "odt",
    "xls",
    "xlsx",
    "ods",
    "pptx",
    "odp",
];

/** Why a file will not be sent or kept. */
export type MeetingFileRejection = "empty" | "too-large" | "type";

/** Where a transfer stands. */
export type MeetingFileStatus = "transferring" | "done" | "failed";

export type MeetingFileEntry = {
    /** The stream id, or a local id for a file this side is sending. */
    id: string;
    /** Milliseconds since the epoch, as the sender stamped it. */
    timestamp: number;
    /** Already made safe to save under. */
    name: string;
    /** Bytes, when known. */
    size: number | undefined;
    authorId: string;
    authorName: string;
    mine: boolean;
    status: MeetingFileStatus;
    /** Whole percent, 0–100. */
    progress: number;
    /** Where a received file can be downloaded from, once it has all arrived. */
    url?: string;
};

function extensionOf(name: string): string {
    const dot = name.lastIndexOf(".");
    return dot < 0 ? "" : name.slice(dot + 1).toLowerCase();
}

/**
 * Whether a file may cross, judged by its name and size.
 *
 * Checked by the sender before anything goes out and again by the receiver on
 * arrival, since the receiver cannot trust the other client. An unknown size
 * passes here; the receiver counts the bytes as they come instead.
 */
export function meetingFileRejection(file: {
    name: string;
    size: number | undefined;
}): MeetingFileRejection | null {
    if (file.size !== undefined && file.size <= 0) return "empty";
    if (file.size !== undefined && file.size > MAX_MEETING_FILE_BYTES) return "too-large";
    if (!ALLOWED_MEETING_FILE_EXTENSIONS.includes(extensionOf(file.name))) return "type";
    return null;
}

/**
 * A name from the other side, made safe to save under.
 *
 * Only the last path segment is kept, characters a file system refuses are
 * removed, and a long name is shortened in front of its extension so the
 * saved file still opens with the right program. Returns "" when nothing
 * usable is left; the caller supplies a fallback in the reader's language.
 */
export function safeFileName(raw: string): string {
    const base = raw.split(/[/\\]/).pop() ?? "";
    // eslint-disable-next-line no-control-regex
    const clean = base.replace(/[ -<>:"|?*]/g, "").trim();
    if (clean.length <= MAX_MEETING_FILE_NAME_LENGTH) return clean;

    const extension = extensionOf(clean);
    const suffix = extension ? `.${extension}` : "";
    const stem = clean.slice(0, clean.length - suffix.length);
    return stem.slice(0, MAX_MEETING_FILE_NAME_LENGTH - suffix.length) + suffix;
}

/**
 * Whole percent from LiveKit's 0–1 progress, so a transfer re-renders at most
 * a hundred times rather than once per chunk.
 */
export function progressPercent(progress: number | undefined): number {
    if (progress === undefined || !Number.isFinite(progress)) return 0;
    return Math.min(100, Math.max(0, Math.floor(progress * 100)));
}
